import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import type { OnlineStatus } from '@bruno/shared';
import { useAuth } from '../auth/AuthContext';
import { TwoFactorNudgeBanner } from './TwoFactorNudgeBanner';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `px-3 py-2 rounded-md text-sm font-medium ${
    isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-100'
  }`;

export function Layout() {
  const { user, logout, setStatus } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === 'owner';
  const isTeamMember = user?.role === 'owner' || user?.role === 'agent';

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/bruno-logo.svg" alt="Bruno" className="h-8 w-8" />
            <span className="font-semibold text-slate-900">Bruno</span>
          </Link>
          <nav className="flex items-center gap-1">
            {isTeamMember ? (
              <NavLink to="/inbox" className={navLinkClass}>
                Inbox
              </NavLink>
            ) : null}
            {isOwner ? (
              <>
                <NavLink to="/websites" className={navLinkClass}>
                  Websites
                </NavLink>
                <NavLink to="/team" className={navLinkClass}>
                  Team
                </NavLink>
                <NavLink to="/settings/handoff" className={navLinkClass}>
                  Handoff
                </NavLink>
                <NavLink to="/billing" className={navLinkClass}>
                  Billing
                </NavLink>
              </>
            ) : null}
            {isTeamMember ? (
              <NavLink to="/settings/telegram" className={navLinkClass}>
                Telegram
              </NavLink>
            ) : null}
            <NavLink to="/settings/account" className={navLinkClass}>
              Account
            </NavLink>
          </nav>
          <div className="flex items-center gap-3">
            {isTeamMember ? (
              <PresenceMenu
                status={user?.onlineStatus ?? 'offline'}
                onChange={(s) => void setStatus(s)}
              />
            ) : null}
            <span className="text-sm text-slate-500">{user?.email}</span>
            <button
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Logout
            </button>
          </div>
        </div>
      </header>
      <TwoFactorNudgeBanner />
      <main className="flex-1 mx-auto w-full max-w-6xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}

function PresenceMenu({
  status,
  onChange,
}: {
  status: OnlineStatus;
  onChange: (s: OnlineStatus) => void;
}) {
  const dot =
    status === 'online'
      ? 'bg-emerald-500'
      : status === 'away'
        ? 'bg-amber-500'
        : 'bg-slate-300';
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot}`} aria-hidden />
      <select
        value={status}
        onChange={(e) => onChange(e.target.value as OnlineStatus)}
        className="bg-transparent text-sm text-slate-700 focus:outline-none"
      >
        <option value="online">Online</option>
        <option value="away">Away</option>
        <option value="offline">Offline</option>
      </select>
    </label>
  );
}
